import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Star as StarFilledIcon,
  StarBorder as StarOutlineIcon,
} from '@mui/icons-material';
import DepartureTopStrip from '../components/ui/DepartureTopStrip';
import PhoneTopStrip from '../components/ui/PhoneTopStrip';
import useRenderMode from '../hooks/useRenderMode';
import { useZones } from '../hooks/useZones';
import { fetchFeedbackPage, fetchAllFeedbacksWithFilters, fetchAllSupervisors } from './FeedbackPaginationUtils';
import { resolveSupervisorIxoraIds } from '../utils/supervisorIds';
import { getCountFromServer, query, where } from 'firebase/firestore';
import { feedbackCollection } from '../firebase';

// Total count minus silent_duplicate count = legitimate count.  Two server-side
// aggregations are still O(1) Firestore reads.
async function getLegitimateCount() {
  const [totalSnap, silentSnap] = await Promise.all([
    getCountFromServer(feedbackCollection),
    getCountFromServer(query(feedbackCollection, where('silent_duplicate', '==', true))),
  ]);
  return Math.max(0, totalSnap.data().count - silentSnap.data().count);
}
import { downloadFeedbacksByDate } from '../utils/downloadDayReports';

function formatDateTimeWithTime(dateObj) {
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  const day = dateObj.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear().toString().slice(-2);
  const time = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return `${day}${getOrdinalSuffix(day)} ${month} ${year} · ${time}`;
}

// Zone name <-> area number lookups, sourced from Firestore via useZones().
// Old code paths that don't have a hook (callbacks called from button onClick
// before the hook reruns) take the maps as args, keeping behaviour pure.
function zoneNameByAreaNum(zoneByAreaNumber, areaNum) {
  const z = zoneByAreaNumber.get(areaNum);
  return z ? z.name : 'Unknown Area';
}

function zoneAreaNumByName(zones, areaName) {
  // Aliases that existed in the old ladder; preserved so existing supervisor
  // docs with ixora_allotment like "Police Station" still resolve.
  const ALIASES = {
    'Police Station': 'Police Stations - Hyderabad',
    'Police Station Hyderabad': 'Police Stations - Hyderabad',
    'Police Station Cyberabad': 'Police Stations - Cyberabad',
  };
  const canonical = ALIASES[areaName] ?? areaName;
  const z = zones.find((zone) => zone.name === canonical);
  return z ? z.areaNumber : null;
}

function parseLocalDateInput(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const Feedback = () => {
  const mode = useRenderMode();
  const { zones, zoneByAreaNumber } = useZones();
  const areaOptions = useMemo(() => zones.map((z) => z.name), [zones]);

  // Pagination + data state ------------------------------------------------
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [prevStack, setPrevStack] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);

  // Filter state -----------------------------------------------------------
  const [filterArea, setFilterArea] = useState('');
  const [filterSupervisor, setFilterSupervisor] = useState('');
  const [supervisors, setSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const loadRequestRef = useRef(0);
  const isFiltering = Boolean(filterArea || filterSupervisor);

  useEffect(() => {
    const loadSupervisors = async () => {
      setLoadingSupervisors(true);
      try {
        const supervisorsList = await fetchAllSupervisors();
        supervisorsList.sort((a, b) => {
          if (!a.ixora_user_id) return 1;
          if (!b.ixora_user_id) return -1;
          return a.ixora_user_id.localeCompare(b.ixora_user_id, undefined, { numeric: true, sensitivity: 'base' });
        });
        setSupervisors(supervisorsList);
      } catch (error) {
        console.error('Error loading supervisors:', error);
      } finally {
        setLoadingSupervisors(false);
      }
    };
    loadSupervisors();
  }, []);

  async function loadTotalCount() {
    setTotalCount(await getLegitimateCount());
  }

  useEffect(() => {
    loadTotalCount();
    loadFirstPage();
  }, [pageSize]);

  async function loadFirstPage() {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    try {
      if (isFiltering) {
        const areaNum = filterArea ? zoneAreaNumByName(zones, filterArea) : null;
        const supervisorId = filterSupervisor || null;
        const results = await fetchAllFeedbacksWithFilters({ areaNum, supervisorId });
        if (requestId !== loadRequestRef.current) return;
        setFeedbackItems(results);
        setLastVisible(null);
        setTotalCount(results.length);
      } else {
        const { feedbacks, lastVisible: newLastVisible } = await fetchFeedbackPage({ pageSize });
        if (requestId !== loadRequestRef.current) return;
        setFeedbackItems(feedbacks);
        setLastVisible(newLastVisible);
        if (totalCount === 0) {
          setTotalCount(await getLegitimateCount());
        }
      }
      setPrevStack([]);
      setPage(1);
    } catch (error) {
      console.error('Error loading first page:', error);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }

  async function loadNextPage() {
    if (loading) return;
    if (isFiltering) {
      const startIndex = page * pageSize;
      if (startIndex >= feedbackItems.length) return;
      setPage((p) => p + 1);
      return;
    }
    const currentCursor = lastVisible;
    if (!currentCursor) return;
    setLoading(true);
    try {
      const { feedbacks, lastVisible: newLastVisible } = await fetchFeedbackPage({ pageSize, lastVisible: currentCursor });
      setPrevStack((prev) => [...prev, currentCursor]);
      setFeedbackItems(feedbacks);
      setLastVisible(newLastVisible);
      setPage((p) => p + 1);
    } catch (error) {
      console.error('Error loading next page:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadPrevPage() {
    if (loading) return;
    if (isFiltering) {
      if (page <= 1) return;
      setPage((p) => p - 1);
      return;
    }
    const currentStack = prevStack;
    if (currentStack.length === 0) return;
    setLoading(true);
    try {
      const prevCursor = currentStack[currentStack.length - 2] || null;
      const { feedbacks, lastVisible: newLastVisible } = await fetchFeedbackPage({ pageSize, lastVisible: prevCursor });
      setFeedbackItems(feedbacks);
      setLastVisible(newLastVisible);
      setPrevStack((prev) => prev.slice(0, -1));
      setPage((p) => p - 1);
    } catch (error) {
      console.error('Error loading previous page:', error);
    } finally {
      setLoading(false);
    }
  }

  const applyFilters = async () => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    try {
      const areaNum = filterArea ? zoneAreaNumByName(zones, filterArea) : null;
      // Resolve legacy ids from the selected supervisor doc so historical
      // feedback (stamped with the old ixora_user_id) is included.
      const selectedSupervisor = filterSupervisor
        ? supervisors.find((s) => s.ixora_user_id === filterSupervisor)
        : null;
      const supervisorIds = selectedSupervisor
        ? resolveSupervisorIxoraIds(selectedSupervisor)
        : (filterSupervisor ? [filterSupervisor] : []);
      const results = await fetchAllFeedbacksWithFilters({ areaNum, supervisorIds });
      if (requestId !== loadRequestRef.current) return;
      setFeedbackItems(results);
      setLastVisible(null);
      setPrevStack([]);
      setPage(1);
      setTotalCount(results.length);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  };

  const resetFilters = async () => {
    const requestId = ++loadRequestRef.current;
    setFilterArea('');
    setFilterSupervisor('');
    setLoading(true);
    try {
      const { feedbacks, lastVisible: newLastVisible } = await fetchFeedbackPage({ pageSize });
      if (requestId !== loadRequestRef.current) return;
      setFeedbackItems(feedbacks);
      setLastVisible(newLastVisible);
      setPrevStack([]);
      setPage(1);
      setTotalCount(await getLegitimateCount());
    } catch (error) {
      console.error('Error resetting filters:', error);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedDate) {
      alert('Please select a date to download feedbacks.');
      return;
    }
    const dateObj = parseLocalDateInput(selectedDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (dateObj > today) {
      alert('Cannot download feedbacks for future dates.');
      return;
    }
    setIsDownloading(true);
    try {
      const success = await downloadFeedbacksByDate(dateObj);
      if (success) console.log('Download completed successfully');
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const getTodayString = () => new Date().toISOString().split('T')[0];

  const renderStars = (n) => (
    <div style={{ display: 'flex', color: 'var(--c-brand-600)' }} aria-label={`${n} of 5`}>
      {[1, 2, 3, 4, 5].map((s) => s <= n
        ? <StarFilledIcon key={s} style={{ fontSize: 16 }} />
        : <StarOutlineIcon key={s} style={{ fontSize: 16, color: 'var(--c-line-2)' }} />)}
    </div>
  );

  // Compute the slice for the current page when filtering (already-loaded set)
  const displayedItems = (isFiltering && feedbackItems.length > pageSize)
    ? feedbackItems.slice((page - 1) * pageSize, Math.min((page - 1) * pageSize + pageSize, feedbackItems.length))
    : feedbackItems;

  // --- Phone mode: Departure Board phone variant -------------------------
  if (mode === 'phone') {
    const fmt = (v) => {
      if (!v) return '';
      try {
        const d = v.seconds ? new Date(v.seconds * 1000) : new Date(v);
        return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      } catch { return ''; }
    };
    return (
      <div className="db-page-phone">
        <PhoneTopStrip />

        <div className="db-ribbon-phone">
          <div className="title">
            ALL FEEDBACK · <em>HYDERABAD</em>
            <div style={{ marginTop: 4, color: 'var(--c-ink-3)', letterSpacing: '.10em', fontWeight: 500 }}>
              {totalCount.toLocaleString('en-IN')} ENTRIES
            </div>
          </div>
          <div className="pills" style={{ flexWrap: 'wrap', overflowX: 'visible' }}>
            <button type="button" className="db-pill" onClick={resetFilters}>RESET</button>
            <button type="button" className="db-pill primary" onClick={applyFilters}>APPLY</button>
            <button type="button" className="db-pill" onClick={handleDownload} disabled={!selectedDate || isDownloading}>
              {isDownloading ? 'DOWNLOADING…' : 'CSV ↓'}
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            <div className="db-form-row">
              <label>AREA</label>
              <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)}>
                <option value="">ALL AREAS</option>
                {areaOptions.map((a) => <option key={a} value={a}>{a.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="db-form-row">
              <label>SUPERVISOR {loadingSupervisors && '· LOADING'}</label>
              <select value={filterSupervisor} onChange={(e) => setFilterSupervisor(e.target.value)} disabled={loadingSupervisors}>
                <option value="">ALL SUPERVISORS</option>
                {supervisors.map((s) => (
                  <option key={s.id} value={s.ixora_user_id || ''}>
                    {(s.display_name || 'UNKNOWN').toUpperCase()} · {s.ixora_user_id || '—'}
                  </option>
                ))}
              </select>
            </div>
            <div className="db-form-row" style={{ marginBottom: 0 }}>
              <label>DATE (FOR DOWNLOAD)</label>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} max={getTodayString()} />
            </div>
          </div>
        </div>

        {loading && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            margin: '14px 0', gap: 10,
            fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em',
            textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 600,
          }}>
            <div className="spinner" />
            <span>LOADING FEEDBACK…</span>
          </div>
        )}

        {!loading && displayedItems.length === 0 ? (
          <div style={{
            padding: '32px 16px', textAlign: 'center', color: 'var(--c-ink-3)',
            fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em',
            textTransform: 'uppercase', fontWeight: 600,
          }}>
            {isFiltering ? 'NO MATCHING FEEDBACK' : 'NO FEEDBACK YET'}
          </div>
        ) : (
          <div className="db-list-phone">
            {displayedItems.map((item) => (
              <div key={item.id} className="item">
                <div className="row1">
                  {renderStars(parseInt(item.feedback_stars) || 0)}
                  <div className="nm" style={{ flex: 1, textAlign: 'right' }}>{item.feedback_collector_name || '—'}</div>
                </div>
                <div className="meta">
                  {item.shop_name || '—'} · {item.shop_area ? zoneNameByAreaNum(zoneByAreaNumber, parseInt(item.shop_area)).toUpperCase() : '—'}
                </div>
                <div className="sub">
                  {fmt(item.feedback_collection_DateTime)}
                  {item.phone_number ? ` · ${item.phone_number}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px', gap: 12, flexWrap: 'wrap',
          borderTop: '1px solid var(--c-line-hard)',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 10,
            letterSpacing: '.12em', textTransform: 'uppercase',
            color: 'var(--c-ink-3)', fontWeight: 600,
          }}>
            {feedbackItems.length > 0
              ? (isFiltering
                ? `${Math.min((page - 1) * pageSize + 1, feedbackItems.length)}–${Math.min(page * pageSize, feedbackItems.length)} / ${totalCount.toLocaleString('en-IN')}`
                : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} / ${totalCount.toLocaleString('en-IN')}`)
              : `0 / ${totalCount.toLocaleString('en-IN')}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="db-pill" onClick={loadPrevPage}
              disabled={isFiltering ? page <= 1 : prevStack.length === 0}>← PREV</button>
            <button type="button" className="db-pill primary" onClick={loadNextPage}
              disabled={isFiltering ? page * pageSize >= feedbackItems.length : (feedbackItems.length < pageSize || feedbackItems.length === totalCount)}>NEXT →</button>
          </div>
        </div>
      </div>
    );
  }

  // --- Desk / wall: Departure Board ---------------------------------------
  return (
    <div className="db-page">
      <DepartureTopStrip />

      <div className="db-ribbon">
        <div className="db-ribbon-title">
          ALL FEEDBACK · <em>HYDERABAD</em>{' '}
          <span style={{ color: 'var(--c-ink-3)', fontWeight: 500, letterSpacing: '.10em', marginLeft: 12 }}>
            {totalCount.toLocaleString('en-IN')} ENTRIES
          </span>
        </div>
        <div className="db-ribbon-actions">
          <select
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            style={{
              width: 'auto', minHeight: 32, padding: '4px 10px',
              fontFamily: 'var(--font-mono)', fontSize: 11,
              letterSpacing: '.10em', textTransform: 'uppercase',
              border: '1px solid var(--c-line-2)', borderRadius: 0,
              background: 'var(--c-bg)', color: 'var(--c-ink-2)', fontWeight: 600,
            }}
          >
            <option value="">AREA · ALL</option>
            {areaOptions.map((a) => (
              <option key={a} value={a}>{a.toUpperCase()}</option>
            ))}
          </select>
          <select
            value={filterSupervisor}
            onChange={(e) => setFilterSupervisor(e.target.value)}
            disabled={loadingSupervisors}
            style={{
              width: 'auto', minHeight: 32, padding: '4px 10px',
              fontFamily: 'var(--font-mono)', fontSize: 11,
              letterSpacing: '.10em', textTransform: 'uppercase',
              border: '1px solid var(--c-line-2)', borderRadius: 0,
              background: 'var(--c-bg)', color: 'var(--c-ink-2)', fontWeight: 600,
              maxWidth: 240,
            }}
          >
            <option value="">SUPERVISOR · ALL</option>
            {supervisors.map((s) => (
              <option key={s.id} value={s.ixora_user_id || ''}>
                {(s.display_name || 'UNKNOWN').toUpperCase()} · {s.ixora_user_id || '—'}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={getTodayString()}
            style={{
              width: 'auto', minHeight: 32, padding: '4px 10px',
              fontFamily: 'var(--font-mono)', fontSize: 11,
              letterSpacing: '.04em', border: '1px solid var(--c-line-2)',
              borderRadius: 0, background: 'var(--c-bg)', color: 'var(--c-ink-2)',
              fontWeight: 600,
            }}
            aria-label="Date for CSV download"
          />
          <button type="button" className="db-pill" onClick={handleDownload} disabled={!selectedDate || isDownloading}>
            {isDownloading ? 'DOWNLOADING…' : 'CSV ↓'}
          </button>
          <button type="button" className="db-pill" onClick={resetFilters}>RESET</button>
          <button type="button" className="db-pill primary" onClick={applyFilters}>APPLY</button>
        </div>
      </div>

      <section style={{ padding: 'var(--sp-6) var(--pad-h) var(--sp-12)', overflowX: 'auto' }}>
        {loading && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            margin: '12px 0 16px', gap: 10,
            fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.14em',
            textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 600,
          }}>
            <div className="spinner" />
            <span>LOADING FEEDBACK…</span>
          </div>
        )}

        {displayedItems.length === 0 && !loading ? (
          <div style={{
            padding: 'var(--sp-12) 0', textAlign: 'center',
            color: 'var(--c-ink-3)', fontFamily: 'var(--font-mono)',
            fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase',
            fontWeight: 600,
          }}>
            {isFiltering ? 'NO MATCHING FEEDBACK · CLEAR FILTERS TO RESET' : 'NO FEEDBACK YET'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="db-table" style={{ minWidth: 1100 }}>
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>TIME</th>
                  <th className="num">SUP · ID</th>
                  <th>SUPERVISOR</th>
                  <th>AREA</th>
                  <th>RATING</th>
                  <th className="num">PHONE</th>
                  <th>SHOP</th>
                  <th>ADDRESS</th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="num">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      {item.feedback_collection_DateTime
                        ? formatDateTimeWithTime(new Date(item.feedback_collection_DateTime.seconds * 1000))
                        : '—'}
                    </td>
                    <td className="num">{item.feedback_collector_ixora_userID || '—'}</td>
                    <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={item.feedback_collector_name || undefined}
                    >{item.feedback_collector_name || '—'}</td>
                    <td>{item.shop_area ? zoneNameByAreaNum(zoneByAreaNumber, parseInt(item.shop_area)) : '—'}</td>
                    <td>{item.feedback_stars != null ? renderStars(parseInt(item.feedback_stars) || 0) : '—'}</td>
                    <td className="num">{item.phone_number ? String(item.phone_number) : '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={item.shop_name || undefined}
                    >{item.shop_name || '—'}</td>
                    <td style={{ color: 'var(--c-ink-3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={item.shop_address || undefined}
                    >{item.shop_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          margin: '24px 0 0', gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            letterSpacing: '.14em', textTransform: 'uppercase',
            color: 'var(--c-ink-3)', fontWeight: 600,
          }}>
            {feedbackItems.length > 0
              ? (isFiltering
                ? `${Math.min((page - 1) * pageSize + 1, feedbackItems.length)}–${Math.min(page * pageSize, feedbackItems.length)} OF ${totalCount.toLocaleString('en-IN')}`
                : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} OF ${totalCount.toLocaleString('en-IN')}`)
              : `0 OF ${totalCount.toLocaleString('en-IN')}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="db-pill" onClick={loadPrevPage}
              disabled={isFiltering ? page <= 1 : prevStack.length === 0}>← PREV</button>
            <button type="button" className="db-pill primary" onClick={loadNextPage}
              disabled={isFiltering ? page * pageSize >= feedbackItems.length : (feedbackItems.length < pageSize || feedbackItems.length === totalCount)}>NEXT →</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Feedback;
